import { Container, Graphics, Rectangle, type Renderer } from 'pixi.js';
import * as THREE from 'three';

export interface BakedSprite {
  texture: THREE.CanvasTexture;
  canvas: HTMLCanvasElement;
}

/**
 * Rasterizes procedurally-drawn Pixi Graphics into reusable HTMLCanvas-backed
 * Three.js textures. One scratch Container is reused across all bakes.
 */
export class SpriteBaker {
  private readonly scratch = new Container();
  private readonly ctxCache = new WeakMap<HTMLCanvasElement, CanvasRenderingContext2D>();

  constructor(private readonly renderer: Renderer) {}

  createSprite(canvasPx: number): BakedSprite {
    const canvas = document.createElement('canvas');
    canvas.width = canvasPx;
    canvas.height = canvasPx;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.premultiplyAlpha = false;
    texture.anisotropy = 4;
    return { texture, canvas };
  }

  /**
   * Rebake a sprite. `draw` populates one or more Graphics attached to the
   * scratch container. The world-space rectangle [-halfExtent..+halfExtent]²
   * is captured into the sprite's canvas.
   */
  bake(
    sprite: BakedSprite,
    halfExtent: number,
    draw: (scratch: Container) => void,
  ): void {
    // Clear previous children
    while (this.scratch.children.length > 0) {
      const c = this.scratch.children[0]!;
      this.scratch.removeChild(c);
      if (c instanceof Graphics) c.destroy();
      else c.destroy({ children: true });
    }

    draw(this.scratch);

    const src = this.renderer.extract.canvas({
      target: this.scratch,
      frame: new Rectangle(-halfExtent, -halfExtent, halfExtent * 2, halfExtent * 2),
      antialias: true,
    });

    const dst = sprite.canvas;
    let ctx = this.ctxCache.get(dst);
    if (!ctx) {
      ctx = dst.getContext('2d')!;
      this.ctxCache.set(dst, ctx);
    }
    ctx.clearRect(0, 0, dst.width, dst.height);
    ctx.drawImage(src as CanvasImageSource, 0, 0, dst.width, dst.height);
    sprite.texture.needsUpdate = true;
  }

  disposeSprite(sprite: BakedSprite): void {
    sprite.texture.dispose();
  }

  dispose(): void {
    while (this.scratch.children.length > 0) {
      const c = this.scratch.children[0]!;
      this.scratch.removeChild(c);
      c.destroy({ children: true });
    }
    this.scratch.destroy();
  }
}
